// NavHistoryContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  useNavigate,
  useLocation,
  useNavigationType,
  NavigationType,
} from 'react-router-dom';

export const NavHistoryContext = createContext(null);

/** Routes where back should return to the main client list */
export const ROOT_BACK_PATHS = ['/client-details'];

/* Routes whose sub-paths collapse into ONE stack entry */
const COLLAPSE_PREFIXES = ['/document', '/otherdocument'];
const sharesCollapsiblePrefix = (a = '', b = '') =>
  COLLAPSE_PREFIXES.some((p) => a.startsWith(p) && b.startsWith(p));

const toNavigateTarget = (entry) => {
  if (!entry?.pathname) return null;
  return {
    pathname: entry.pathname,
    search: entry.search ?? '',
    hash: entry.hash ?? '',
  };
};

export const NavHistoryProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const [historyStack, setHistoryStack] = useState([location]);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    setHistoryStack((prev) => {
      const last = prev[prev.length - 1];

      switch (navigationType) {
        case NavigationType.Push: {
          if (
            sharesCollapsiblePrefix(
              last.pathname.toLowerCase(),
              location.pathname.toLowerCase()
            )
          ) {
            const next = [...prev];
            next[next.length - 1] = location;
            return next;
          }

          if (last.pathname === location.pathname && last.key === location.key) {
            return prev;
          }
          return [...prev, location];
        }

        case NavigationType.Replace: {
          const next = [...prev];
          next[next.length - 1] = location;
          return next;
        }

        case NavigationType.Pop: {
          const idx = prev.findIndex((e) => e.key === location.key);
          return idx !== -1 ? prev.slice(0, idx + 1) : [location];
        }

        default:
          return prev;
      }
    });
  }, [location, navigationType]);

  /**
   * Navigate back using, in order:
   * 1) location.state.previousLocation (explicit in-app parent route)
   * 2) custom history stack (actual visited routes)
   * 3) client-details when a client is known
   * 4) browser history / Main
   */
  const navigateBack = useCallback(
    ({ currentLocation, client } = {}) => {
      const loc = currentLocation ?? location;
      const path = (loc.pathname || '').toLowerCase();

      if (ROOT_BACK_PATHS.includes(path)) {
        navigate('/Main', { replace: true });
        return;
      }

      const statePrev = loc.state?.previousLocation;
      const stateTarget = toNavigateTarget(statePrev);
      if (stateTarget) {
        navigate(stateTarget, {
          state: statePrev.state ?? null,
          replace: true,
        });
        return;
      }

      if (historyStack.length > 1) {
        const target = historyStack[historyStack.length - 2];
        const routeTarget = toNavigateTarget(target);
        if (routeTarget) {
          setHistoryStack((prev) => prev.slice(0, -1));
          navigate(routeTarget, {
            state: target.state ?? null,
            replace: true,
          });
          return;
        }
      }

      const clientForBack = client ?? loc.state?.client;
      if (clientForBack) {
        navigate('/client-details', {
          replace: true,
          state: { client: clientForBack, breadcrumbs: ['ClientPage'] },
        });
        return;
      }

      if (window.history.length > 1) {
        navigate(-1);
        return;
      }

      navigate('/Main', { replace: true });
    },
    [historyStack, location, navigate]
  );

  const goBack = useCallback(
    (options) => navigateBack(options),
    [navigateBack]
  );

  return (
    <NavHistoryContext.Provider value={{ historyStack, goBack, navigateBack }}>
      {children}
    </NavHistoryContext.Provider>
  );
};
